import React, { Fragment, useState, useEffect } from "react";
import { connect } from "react-redux";
import { func, array, any } from "prop-types";
import { uploadDocumentation, clearErrors } from "../../redux/actions";
import DocumentationFileList from "../../components/Documentation/DocumentationFileList";
import DocumentationForm from "../../components/Documentation/DocumentationForm";
import Popup from "../../common/Popup/Popup";
import UploadingError from "../../components/FileUploadStatuses/UploadingError";
import UploadingSuccess from "../../components/FileUploadStatuses/UploadingSuccess";
import { isEmpty, isMobile } from "../../helpers";
import history from "../../history";
import * as style from "./styles.module.scss";
import ImageLoader from "../../components/ImageLoader";
import { InputFile } from "../../common";
import { useWindowDimensions } from "../../helpers/hooks";

const fileStatus = Object.freeze({
  Initial: 1,
  Error: 2,
  Success: 3,
});

const useDocumentationState = ({
  fileTypeCode = "",
  displayName = "",
  documentFile = null,
}) => {
  const initialState = {
    fileTypeCode,
    displayName,
    documentFile,
  };

  const [documentationState, setDocumentationState] = useState(initialState);

  const onChange = (key, val) => {
    setDocumentationState((state) => ({
      ...state,
      [key]: val,
    }));
  };

  const setType = (name, fileType) =>
    setDocumentationState((state) => ({
      ...state,
      fileTypeCode: fileType,
      displayName: name,
    }));

  const resetState = () => {
    setDocumentationState(initialState);
  };

  return [documentationState, onChange, setType, resetState];
};

const calculateFileUploadStatus = (errors, isSubmitted) => {
  const documentationErrors =
    errors &&
    errors.filter((error) => error.source === "Documentation").length > 0;

  if (!isSubmitted) return fileStatus.Initial;

  if (documentationErrors) return fileStatus.Error;

  if (!documentationErrors) return fileStatus.Success;

  return fileStatus.Initial;
};

const renderSwitch = ({
  documentationFiles,
  handleCloseClick,
  handleSubmit,
  tryAgain,
  uploadStatus,
  state,
  setType,
  handleChange,
  handleInvalidType,
  isFileValid,
  isFileListVisible,
  setFileListVisible,
  isUpdating,
  height,
}) => {
  const isValid = () => {
    const requiredFields = ["fileTypeCode", "documentFile"];
    return requiredFields.reduce(
      (acc, key) => acc && !isEmpty(state[key]),
      true
    );
  };

  const calculateScrollablePartHeight = () => {
    const header = document.getElementById("popup-header");
    const buttons = document.getElementById("popup-buttons");
    const popupHeight = height * 0.9;
    const headerAndPaddingTop = header ? header.offsetHeight : 115;
    const buttonsAndPaddings = buttons ? buttons.offsetHeight : 90;

    return popupHeight - headerAndPaddingTop - buttonsAndPaddings;
  };

  switch (uploadStatus) {
    case fileStatus.Initial:
      return (
        <div className={style.documentation}>
          <header id="popup-header" className={style.documentation__header}>
            <h4 className={style.documentation__title}>
              {isUpdating ? "Update" : "Upload"} your documentation
            </h4>
            {(!isUpdating || (!isFileListVisible && isMobile)) && (
              <p className="fontSize-18">
                Your HRGO consultant will inform you what documents to submit.
              </p>
            )}
          </header>
          <section className={style.documentation__card}>
            <div
              className="overflow-auto px-1"
              style={{ maxHeight: `${calculateScrollablePartHeight()}px` }}
            >
              {(!isMobile ||
                (isMobile && !(isFileListVisible && isUpdating))) && (
                <DocumentationForm
                  isUpdating={isUpdating}
                  handleChange={handleChange}
                  setType={setType}
                  state={state}
                  isFileValid={isFileValid}
                  handleInvalidType={handleInvalidType}
                />
              )}
              {isUpdating && ((isMobile && isFileListVisible) || !isMobile) && (
                <DocumentationFileList
                  documentationFiles={documentationFiles}
                />
              )}
            </div>

            <div
              id="popup-buttons"
              className="d-flex justify-content-end flex-sm-row flex-column-reverse mx-1 mx-sm-0 px-sm-1"
            >
              <button
                className="btn btn-outline-dark mt-1 my-sm-2 ml-sm-2 col-12 col-sm-3 col-lg-2"
                onClick={handleCloseClick}
                style={{ minWidth: "175px" }}
              >
                Back to My Account
              </button>
              {isMobile &&
                (!state.documentFile || !isFileValid) &&
                !(isFileListVisible && isUpdating) && (
                  <ImageLoader
                    name="documentFile"
                    handleChange={handleChange}
                    validTypes=".jpg,.jpeg,.png"
                    handleInvalidType={handleInvalidType}
                    render={({ handleImageSelect, name }) => (
                      <InputFile
                        onChangeHandler={handleImageSelect}
                        acceptMimeTypes="image/*"
                        classes={`btn btn-block btn-primary my-sm-2 ml-sm-2 col-12 col-sm-11 col-lg-2 ${
                          isEmpty(state.fileTypeCode) ? "disabled" : ""
                        }`}
                        idName={name}
                        text={
                          isFileValid
                            ? "Choose your document"
                            : "Choose a different document"
                        }
                        disabled={isEmpty(state.fileTypeCode)}
                      />
                    )}
                  />
                )}
              {(!isMobile || (isMobile && state.documentFile)) && (
                <button
                  className="btn btn-primary mt-1 my-sm-2 ml-sm-2 col-12 col-sm-3 col-lg-2"
                  onClick={() => handleSubmit(state)}
                  disabled={!isValid()}
                >
                  Upload
                </button>
              )}
              {isMobile && isUpdating && isFileListVisible && (
                <button
                  className="btn btn-primary my-sm-2 ml-sm-2 col-12 col-sm-4 col-lg-2"
                  onClick={() => setFileListVisible(false)}
                >
                  Add a new document
                </button>
              )}
            </div>
          </section>
        </div>
      );
    case fileStatus.Error:
      return <UploadingError fileCategory="document" onTryAgain={tryAgain} />;
    case fileStatus.Success:
      return (
        <UploadingSuccess
          fileCategory="document"
          isUpdating={documentationFiles && documentationFiles.length > 1}
          onClose={handleCloseClick}
        />
      );
    default:
      return "Something went wrong";
  }
};

const Documentation = ({
  submitDocument,
  documentationFiles,
  errors,
  clearErrorsWithSource,
}) => {
  const [state, handleChange, setType, resetState] = useDocumentationState({});
  const [isFileValid, setIsFileValid] = useState(true);
  const [isSubmitted, setSubmitted] = useState(false);
  const isUpdating = documentationFiles && documentationFiles.length > 0;
  const [isFileListVisible, setFileListVisible] = useState(true);

  const handleCloseClick = () => {
    resetState();
    setSubmitted(false);
    history.push("/profile");
  };

  const handleSubmit = (document) => {
    setSubmitted(true);
    submitDocument(document);
  };

  const tryAgain = () => {
    setSubmitted(false);
    clearErrorsWithSource("Documentation");
  };

  let uploadStatus = calculateFileUploadStatus(errors, isSubmitted);
  useEffect(() => {
    uploadStatus = calculateFileUploadStatus(errors, isSubmitted);
  }, [errors, isSubmitted]);

  const handleInvalidType = () => {
    setIsFileValid(false);
    handleChange("documentFile", null);
  };

  useEffect(() => {
    if (state.documentFile) {
      setIsFileValid(true);
    }
  }, [state.documentFile]);

  const { height } = useWindowDimensions();

  const internals = renderSwitch({
    documentationFiles,
    handleCloseClick,
    handleSubmit,
    tryAgain,
    uploadStatus,
    state,
    handleChange,
    setType,
    handleInvalidType,
    isFileValid,
    isFileListVisible,
    setFileListVisible,
    isUpdating,
    height,
  });

  return (
    <Fragment>
      {isMobile ? (
        internals
      ) : (
        <Popup isOpen={true} handleCloseClick={handleCloseClick} overNav>
          {internals}
        </Popup>
      )}
    </Fragment>
  );
};

Documentation.propTypes = {
  submitDocument: func.isRequired,
  documentationFiles: array,
  errors: any,
  clearErrorsWithSource: any,
};

const mapStateToProps = (state) => ({
  // Filter driving docs
  documentationFiles: state.core.app.documentationFiles.filter(
    (e) =>
      e.TypeCode !== "JDLB" &&
      e.TypeCode !== "JCPC" &&
      e.TypeCode !== "JCPB" &&
      e.TypeCode !== "JDIG"
  ),
  errors: state.core.errors,
});
const mapDispatchToProps = {
  submitDocument: uploadDocumentation,
  clearErrorsWithSource: clearErrors,
};

const withRedux = connect(mapStateToProps, mapDispatchToProps);

export default withRedux(Documentation);
